// -----------------------------------------------------------------------------
//   Utilities
// -----------------------------------------------------------------------------

// In-memory cache for image objects
const imageCache = new Map<string, HTMLImageElement>();
const loadingPromises = new Map<string, Promise<HTMLImageElement>>();

// -----------------------------------------------------------------------------
export function preloadImage(url: string): Promise<HTMLImageElement> {
    // Return cached image if available
    if (imageCache.has(url)) {
        return Promise.resolve(imageCache.get(url)!);
    }

    // Return existing loading promise if image is currently loading
    if (loadingPromises.has(url)) {
        return loadingPromises.get(url)!;
    }

    // Create new loading promise
    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
        // Skip if URL is empty or invalid
        if (!url || url.trim() === '' || url === 'undefined') {
            reject(new Error('Invalid image URL'));
            return;
        }

        const img = new Image();
        
        // Handle successful load
        img.onload = () => {
            imageCache.set(url, img);
            loadingPromises.delete(url);
            resolve(img);
        };

        // Handle error
        img.onerror = () => {
            loadingPromises.delete(url);
            reject(new Error(`Failed to load image: ${url}`));
        };

        // Start loading (triggers browser cache + our memory cache)
        // The browser will cache the image automatically via HTTP cache headers
        img.src = url;
    });

    loadingPromises.set(url, promise);
    return promise;
}

// -----------------------------------------------------------------------------
export function preloadImages(urls: string[]): Promise<HTMLImageElement[]> {
    return Promise.all(urls.map(url => preloadImage(url).catch(() => {
        // Return a placeholder image element on error
        const placeholder = new Image();
        return placeholder;
    })));
}

// -----------------------------------------------------------------------------
export function clearImageCache(url?: string): void {
    if (url) {
        imageCache.delete(url);
        loadingPromises.delete(url);
    } else {
        imageCache.clear();
        loadingPromises.clear();
    }
}

// -----------------------------------------------------------------------------
export function getCachedImage(url: string): HTMLImageElement | null {
    return imageCache.get(url) || null;
}

// -----------------------------------------------------------------------------
export function isImageCached(url: string): boolean {
    return imageCache.has(url);
}
